import { HoverCard, Skeleton, Text, Tooltip } from '@mantine/core';
import type { RunStatus } from '@prisma/client';
import { IconStrikethrough } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { prettyDuration } from '~/common/time/duration';
import { timeAgo } from '~/common/time/timeAgo';
import AiChangeQuickView from '~/components/history/AiChangeQuickView';
import AutohealSuggestionQuickView from '~/components/history/AutohealSuggestionQuickView';
import TestErrorQuickView from '~/components/history/TestErrorQuickView';
import LayeredRunAttemptStatusIcons from '~/components/icons/LayeredRunAttemptStatusIcons';
import { TestTypeIcon } from '~/components/test/TestTypeIcon';
import type { GetSuiteTestRunOutputType } from '~/server/api/routers/test-suite-run-router';
import type { AutohealSuggestion } from '~/types';
import SkipTestInSuiteRunButton from '~/components/testRun/SkipTestInSuiteRun';
import { checkIsErrorAutoHealed } from '~/components/history/ErrorDetails';
import { useProjectId } from '~/hooks/context/useProjectId';
import TrendCell from './TrendCell';
// TODO: Temporarily commented out unused imports while issue filing feature is disabled
// import { ActionIcon, Button, Menu } from '@mantine/core';
// import { modals } from '@mantine/modals';
// import { notifications } from '@mantine/notifications';
// import { IconDotsVertical, IconExternalLink } from '@tabler/icons-react';
// import TestRunAttemptIssueSnippet from '~/components/testRun/TestRunAttemptIssueSnippet';
// import { api } from '~/trpc-extra/api';

export const getEarliestStartTime = (attempts: { startTime: Date }[]) => {
  return attempts.reduce<Date | undefined>(
    (acc, cur) =>
      acc ?
        cur.startTime.getTime() < acc.getTime() ?
          cur.startTime
        : acc
      : cur.startTime,
    undefined,
  );
};

export type TestRunTableItem = GetSuiteTestRunOutputType & {
  showWarning: boolean;
  suiteRunStatus?: RunStatus;
  suiteId: string;
  suiteRunId?: string;
  autohealSuggestion?: AutohealSuggestion;
  isLatestSuiteRun?: boolean;
  isTestSkipped?: boolean;
  issues?: { id: string; issueType: string; status: string }[];
  hasLinearInstalled?: boolean;
  trendData?: {
    suiteRunId: string;
    createdAt: Date;
    testResults: Record<
      string,
      { testRunId: string; status: RunStatus; durationMs: number | null; attemptCount: number }
    >;
  }[];
};

// Cell components that can use hooks
function TestNameCell({ row }: { row: { original: TestRunTableItem } }) {
  const router = useRouter();
  const projectId = useProjectId();
  const testRun = row.original;

  return (
    <div className={testRun.status === 'SKIPPED' || testRun.isTestSkipped ? 'opacity-60' : ''}>
      <div className="flex items-center gap-2">
        {!!testRun.isTestSkipped && testRun.status !== 'SKIPPED' && (
          <Tooltip label="Skipped for future runs">
            <IconStrikethrough
              size={20}
              className="shrink-0 text-gray-500"
            />
          </Tooltip>
        )}
        <Link
          href={{
            pathname: router.pathname,
            query: { ...router.query, testRunDrawer: testRun.id },
          }}
          as={`/project/${projectId}/history/${router.query.runOrSuiteId}/run/${testRun.id}`}
          className="cursor-pointer font-medium hover:text-blue-600 dark:hover:text-blue-400"
        >
          {testRun.test.name}
        </Link>
        {!!testRun.test.testType && testRun.test.testType !== 'WEB' && <TestTypeIcon type={testRun.test.testType} />}
      </div>
    </div>
  );
}

function ErrorAndAiAutoHealCell({ row }: { row: { original: TestRunTableItem } }) {
  // Add the attempt id and index to the ai changes so we can show them by attempt in the quick view
  const allAiChanges = useMemo(() => {
    return row.original.TestRunAttempts.flatMap((attempt, index) =>
      (attempt.executionOutput?.aiChanges || []).map((aiChange) => ({
        ...aiChange,
        attemptId: attempt.id,
        attemptIndex: row.original.TestRunAttempts.length - index,
      })),
    );
  }, [row.original.TestRunAttempts]);

  const allErrors = useMemo(() => {
    return row.original.TestRunAttempts.flatMap((attempt) => attempt.executionOutput?.errors || []);
  }, [row.original.TestRunAttempts]);

  const unhealedErrors = useMemo(() => {
    return row.original.TestRunAttempts.flatMap((attempt, index) =>
      (attempt.executionOutput?.errors || []).map((error) =>
        checkIsErrorAutoHealed({ error, aiChanges: attempt.executionOutput?.aiChanges || [] }) ? undefined : (
          {
            ...error,
            attemptId: attempt.id,
            // Add attempt index information for correct numbering
            // This is needed to match with TestRunAttemptSwitcher.tsx
            attemptIndex: row.original.TestRunAttempts.length - index,
          }
        ),
      ),
    ).filter((error) => error !== undefined);
  }, [row.original.TestRunAttempts]);

  return (
    <div className="flex items-center gap-2">
      <TestErrorQuickView
        errors={unhealedErrors}
        aiChanges={allAiChanges}
        runId={row.original.id}
      />
      <AiChangeQuickView
        aiChanges={allAiChanges}
        runId={row.original.id}
        errors={allErrors}
      />
    </div>
  );
}

function DurationCell({ row }: { row: { original: TestRunTableItem } }) {
  const startTime = useMemo(() => {
    return getEarliestStartTime(row.original.TestRunAttempts);
  }, [row.original.TestRunAttempts]);

  const latestAttemptDuration = row.original.TestRunAttempts.at(-1)?.durationMs ?? 0;

  if (!startTime) {
    return null;
  }

  // Duration can sometimes be negative due to clock sync issues, so we clamp to 0
  const totalDuration = Math.max(0, row.original.durationMs ?? row.original.updatedAt.getTime() - startTime.getTime());

  return (
    <HoverCard
      width={200}
      shadow="md"
      position="bottom"
      withArrow
    >
      <HoverCard.Target>
        <Text
          size="sm"
          c="dimmed"
        >
          {prettyDuration(Math.ceil(latestAttemptDuration / 1000))} / {prettyDuration(Math.ceil(totalDuration / 1000))}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Text
              size="sm"
              className="font-medium"
            >
              Total Duration
            </Text>
            <Text
              size="xs"
              c="dimmed"
            >
              {prettyDuration(Math.ceil(totalDuration / 1000))}
            </Text>
          </div>
          <div className="space-y-1">
            {row.original.TestRunAttempts.map((attempt, index) => (
              <div
                key={attempt.id}
                className="flex items-center justify-between"
              >
                <Text
                  size="xs"
                  c="dimmed"
                >
                  Attempt #{index + 1}
                </Text>
                <Text
                  size="xs"
                  c="dimmed"
                >
                  {prettyDuration(Math.ceil((attempt.durationMs ?? 0) / 1000))}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

function StartTimeCell({ row }: { row: { original: TestRunTableItem } }) {
  const startTime = useMemo(() => {
    return getEarliestStartTime(row.original.TestRunAttempts);
  }, [row.original.TestRunAttempts]);

  // Using a hover card vs. TimeAgo to keep styles more consistent in this table
  // Honestly not 100% convinced on this decision
  return !startTime ? null : (
      <HoverCard
        shadow="md"
        position="bottom"
        withArrow
      >
        <HoverCard.Target>
          <Text
            size="sm"
            c="dimmed"
          >
            {startTime.toLocaleString()}
          </Text>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <div className="space-y-2">
            <Text size="sm">{timeAgo(startTime)}</Text>
          </div>
        </HoverCard.Dropdown>
      </HoverCard>
    );
}

// TODO: ActionsCell is temporarily disabled while issue filing feature is not ready
// function ActionsCell({ row }: { row: { original: TestRunTableItem } }) {
//   const projectId = useProjectId();
//   const router = useRouter();
//   const trpcUtils = api.useUtils();

//   const { isTestSkipped } = row.original;

//   const skipTestMutation = api.testSuite.updateTests.useMutation({
//     onSuccess: async (_, variables) => {
//       const isSkipping = variables.operation === 'skip';
//       notifications.show({
//         title: isSkipping ? 'Test Skipped' : 'Test Unskipped',
//         message:
//           isSkipping ?
//             'This test will be skipped in future test suite runs'
//           : 'This test will be included in future test suite runs',
//         color: 'green',
//       });
//       // Invalidate queries to refresh the data
//       await Promise.allSettled([
//         trpcUtils.testSuite.getRuns.invalidate(),
//         trpcUtils.testSuite.get.invalidate(),
//         trpcUtils.testRun.get.invalidate(),
//         trpcUtils.suiteRun.get.invalidate({ runId: row.original.suiteRunId }),
//       ]);
//     },
//     onError: (error) => {
//       notifications.show({
//         title: 'Error',
//         message: error.message || 'Failed to update the test',
//         color: 'red',
//       });
//     },
//   });

//   const handleToggleSkip = () => {
//     const operation = isTestSkipped ? 'unskip' : 'skip';
//     const actionTitle = isTestSkipped ? 'Unskip Test for Future Runs' : 'Skip Test for Future Runs';
//     const buttonText = isTestSkipped ? 'Unskip for Future Runs' : 'Skip for Future Runs';
//     const buttonColor = isTestSkipped ? 'green' : 'red';

//     modals.open({
//       title: actionTitle,
//       centered: true,
//       children: (
//         <div>
//           <Text
//             size="sm"
//             className="mb-3"
//           >
//             {isTestSkipped ?
//               <>
//                 This will include the test &quot;{row.original.test.name}&quot; in all future runs of this test suite.
//               </>
//             : <>This will skip the test &quot;{row.original.test.name}&quot; in all future runs of this test suite.</>}
//           </Text>
//           <Text
//             size="sm"
//             className="mb-3 text-gray-600 dark:text-gray-400"
//           >
//             {isTestSkipped ?
//               'The test will be executed in future runs.'
//             : "The test won't be executed until it's manually re-enabled."}
//           </Text>
//           <div className="mt-4 flex justify-end gap-2">
//             <Button
//               variant="default"
//               onClick={() => modals.closeAll()}
//             >
//               Cancel
//             </Button>
//             <Button
//               color={buttonColor}
//               onClick={() => {
//                 skipTestMutation.mutate({
//                   projectId: projectId!,
//                   testSuiteId: row.original.suiteId,
//                   testIds: [row.original.test.id],
//                   operation,
//                 });

//                 modals.closeAll();
//               }}
//             >
//               {buttonText}
//             </Button>
//           </div>
//         </div>
//       ),
//     });
//   };

//   return (
//     <div className="flex items-center justify-between gap-2 pr-4">
//       <div className="flex items-center gap-2">
//         <TestRunAttemptIssueSnippet
//           runId={row.original.id}
//           attemptId={row.original.TestRunAttempts.at(-1)?.id ?? ''}
//           issues={row.original.issues}
//           hasLinearInstalled={row.original.hasLinearInstalled}
//           testName={row.original.test.name}
//           errorMessage={row.original.TestRunAttempts.at(-1)?.AttemptError?.message}
//         />
//       </div>
//       <Menu
//         shadow="md"
//         width={150}
//         position="bottom-end"
//         withinPortal={true}
//         zIndex={1000}
//         transitionProps={{ duration: 0 }}
//       >
//         <Menu.Target>
//           <ActionIcon
//             variant="subtle"
//             color="gray"
//           >
//             <IconDotsVertical size={14} />
//           </ActionIcon>
//         </Menu.Target>
//         <Menu.Dropdown>
//           <Menu.Item
//             leftSection={<IconExternalLink size={14} />}
//             component={Link}
//             href={`/project/${projectId}/history/${router.query.runOrSuiteId}/run/${row.original.id}`}
//             target="_blank"
//           >
//             View Details
//           </Menu.Item>
//           <Tooltip
//             label={
//               row.original.status === 'RUNNING' ? 'Cannot skip while test is running'
//               : row.original.status === 'QUEUED' ?
//                 'Cannot skip while test is queued'
//               : undefined
//             }
//             disabled={row.original.status !== 'RUNNING' && row.original.status !== 'QUEUED'}
//           >
//             <Menu.Item
//               leftSection={<IconStrikethrough size={14} />}
//               onClick={handleToggleSkip}
//               disabled={row.original.status === 'RUNNING' || row.original.status === 'QUEUED'}
//             >
//               {isTestSkipped ? 'Unskip' : 'Skip'}
//             </Menu.Item>
//           </Tooltip>
//         </Menu.Dropdown>
//       </Menu>
//     </div>
//   );
// }

// Base columns (currently just doesn't include the suggestions column)
export const testRunColumns = ({
  showSuggestionColumn,
  showTrendColumn,
}: {
  showSuggestionColumn?: boolean;
  showTrendColumn?: boolean;
}): ColumnDef<TestRunTableItem>[] => [
  {
    id: 'status',
    accessorFn: (row) => row.status,
    size: 40,
    header: 'Status',
    cell: ({ row }) => (
      <LayeredRunAttemptStatusIcons
        size={20}
        statusHistory={row.original.statusHistory}
        attempts={row.original.TestRunAttempts}
        runStatus={row.original.status}
      />
    ),
    enableSorting: true,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={20}
          radius="xl"
        />
      ),
    },
  },
  {
    id: 'name',
    accessorFn: (row) => row.test.name,
    size: 250,
    header: 'Test Name',
    cell: ({ row }) => <TestNameCell row={row} />,
    enableHiding: false,
    enableSorting: true,
    meta: {
      skeleton: () => (
        <div className="flex flex-col gap-1">
          <Skeleton
            height={20}
            width={`${Math.random() * 200 + 200}px`}
          />
          <Skeleton
            height={16}
            width={`${Math.random() * 150 + 100}px`}
          />
        </div>
      ),
    },
  },
  {
    id: 'errorAndAiAutoHeal',
    header: 'Error & Auto-heal',
    size: 230,
    cell: ({ row }) => <ErrorAndAiAutoHealCell row={row} />,
    enableSorting: false,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={100}
        />
      ),
    },
  },
  ...(showTrendColumn ?
    [
      {
        id: 'trend',
        header: () => (
          <Tooltip label="Shows test results across last 10 runs. Hover for details, click to view run.">
            <span>Trend</span>
          </Tooltip>
        ),
        size: 100,
        minSize: 80,
        cell: ({ row }: { row: { original: TestRunTableItem } }) => {
          return (
            <div className="overflow-hidden">
              <TrendCell
                testId={row.original.test.id}
                trendData={row.original.trendData}
              />
            </div>
          );
        },
        enableSorting: false,
        meta: {
          skeleton: () => (
            <div className="flex items-center gap-0.5 overflow-hidden">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton
                  key={i}
                  height={16}
                  width={6}
                  radius={2}
                />
              ))}
            </div>
          ),
        },
      },
    ]
  : []),
  {
    id: 'duration',
    accessorFn: (row) => row.durationMs,
    size: 90,
    header: 'Duration',
    cell: ({ row }) => <DurationCell row={row} />,
    sortingFn: 'basic',
    sortUndefined: 'last' as const,
    enableSorting: true,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={60}
        />
      ),
    },
  },
  {
    id: 'startTime',
    accessorFn: (row) => getEarliestStartTime(row.TestRunAttempts),
    size: 140,
    header: 'Start Time',
    cell: ({ row }) => <StartTimeCell row={row} />,
    sortingFn: 'datetime',
    enableSorting: true,
    sortUndefined: 'last' as const,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={120}
        />
      ),
    },
  },
  ...(showSuggestionColumn ?
    [
      {
        id: 'suggestion',
        header: 'Suggestion',
        accessorFn: (row: TestRunTableItem) => row.autohealSuggestion,
        size: 180,
        cell: ({ row }: { row: { original: TestRunTableItem } }) => {
          return (
            <div className="flex items-center gap-2">
              <AutohealSuggestionQuickView
                suggestion={row.original.autohealSuggestion}
                latestAttempt={row.original.TestRunAttempts.at(0)}
                suiteRunId={row.original.suiteRunId ?? undefined}
                testRunId={row.original.id}
                showLearnMoreLink={true}
                testStatus={row.original.status}
                isLatestSuiteRun={row.original.isLatestSuiteRun}
              />
              {!!row.original.skipSuggestion && (
                <SkipTestInSuiteRunButton
                  testId={row.original.test.id}
                  testSuiteId={row.original.suiteId}
                  suiteRunId={row.original.suiteRunId}
                />
              )}
            </div>
          );
        },
        sortUndefined: 'last' as const,
        sortingFn: (rowA: { original: TestRunTableItem }, rowB: { original: TestRunTableItem }) => {
          const getSuggestionPriority = (row: TestRunTableItem) => {
            if (row.autohealSuggestion?.shouldSuggest) {
              // Autoheal suggestions (highest priority)
              return 2;
            } else if (row.skipSuggestion) {
              // Skip suggestions (middle priority)
              return 1;
            } else {
              // No suggestions (lowest priority)
              return 0;
            }
          };

          return getSuggestionPriority(rowA.original) - getSuggestionPriority(rowB.original);
        },
        enableSorting: true,
        meta: {
          skeleton: () => (
            <Skeleton
              height={20}
              width={100}
            />
          ),
        },
      },
    ]
  : []),
  // TODO: Issue filing feature is not ready yet - hiding temporarily
  // {
  //   id: 'actions',
  //   enableHiding: false,
  //   enableSorting: false,
  //   size: 120,
  //   header: 'Issues',
  //   cell: ({ row }) => <ActionsCell row={row} />,
  //   meta: {
  //     skeleton: () => (
  //       <div className="flex justify-between gap-2">
  //         <div className="flex gap-2">
  //           <Skeleton
  //             height={20}
  //             width={40}
  //           />
  //           <Skeleton
  //             height={20}
  //             width={60}
  //           />
  //         </div>
  //         <Skeleton
  //           height={20}
  //           width={20}
  //           radius="xl"
  //         />
  //       </div>
  //     ),
  //   },
  // },
];
